import { useForm } from '@tanstack/react-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { toast } from 'sonner';

import { Button } from '@/components/button/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/dialog/dialog';
import { Input } from '@/components/input/input';
import { Label } from '@/components/label/label';
import Select from '@/components/select/select';

import type { BaseDialogProps } from '@/contexts/dialog-context/v2-base-dialog-props';
import { useV2DialogContext } from '@/contexts/dialog-context/v2-dialog-context';
import {
  CreateVoucherSchema,
  createVoucherMutationOptions,
} from '@/features/vouchers/api/create-voucher.api';
import { GET_LIST_VOUCHER_QUERY_KEY } from '@/features/vouchers/api/get-list-voucher.api';
import VoucherProductSelector from './voucher-product-selector';
import VoucherMentorSelector from './voucher-mentor-selector';

const TYPE_OPTIONS = [
  { value: 'FLAT', label: 'Flat (Rupiah)' },
  { value: 'PERCENTAGE', label: 'Percentage (%)' },
];

const TARGET_TYPE_OPTIONS = [
  { value: 'PRODUCT', label: 'Produk' },
  { value: 'MENTORING', label: 'Mentoring' },
];

const scopeModeOptions = (targetType: 'PRODUCT' | 'MENTORING') => [
  {
    value: 'ALL',
    label: targetType === 'PRODUCT' ? 'Semua Produk' : 'Semua Mentor',
  },
  {
    value: 'WHITELIST',
    label: targetType === 'PRODUCT' ? 'Produk Tertentu' : 'Mentor Tertentu',
  },
];

export interface CreateVoucerDialogProps extends BaseDialogProps {}

export default function CreateVoucherDialog({
  dialog,
}: CreateVoucerDialogProps) {
  const { closeCreateVoucherDialog } = useV2DialogContext();
  const queryClient = useQueryClient();

  const createVoucherMutation = useMutation({
    ...createVoucherMutationOptions(),
    onSuccess: () => {
      toast.success('Voucher berhasil ditambahkan');
      queryClient.invalidateQueries({
        queryKey: [GET_LIST_VOUCHER_QUERY_KEY],
      });
      form.reset();
      closeCreateVoucherDialog();
    },
    onError: error => {
      const message =
        error instanceof AxiosError
          ? ((error.response?.data as { message?: string })?.message ??
            error.message)
          : 'Gagal menambahkan voucher';
      toast.error(message);
    },
  });

  const form = useForm({
    defaultValues: {
      voucher_code: '',
      type: 'FLAT' as 'FLAT' | 'PERCENTAGE',
      discount: 0,
      stock: 0,
      expired_at: '',
      expired_at_time: '23:59',
      target_type: 'PRODUCT' as 'PRODUCT' | 'MENTORING',
      scope_mode: 'ALL' as 'ALL' | 'WHITELIST',
      product_ids: [] as string[],
      mentor_ids: [] as string[],
    },
    onSubmit: async ({ value }) => {
      const {
        expired_at_time,
        target_type,
        scope_mode,
        product_ids,
        mentor_ids,
        ...rest
      } = value;
      const isWhitelist = scope_mode === 'WHITELIST';
      const parsed = CreateVoucherSchema.safeParse({
        ...rest,
        expired_at: new Date(
          `${value.expired_at}T${expired_at_time}:00`
        ).toISOString(),
        scopes: [
          {
            target_type,
            mode: scope_mode,
            product_ids:
              target_type === 'PRODUCT' && isWhitelist ? product_ids : [],
            mentors:
              target_type === 'MENTORING' && isWhitelist
                ? mentor_ids.map(mentor_id => ({
                    mentor_id,
                    applies_to_all_topics: true,
                    topic_title_ids: [],
                  }))
                : [],
          },
        ],
      });
      if (!parsed.success) {
        toast.error(
          parsed.error.issues[0]?.message ?? 'Data voucher tidak valid'
        );
        return;
      }
      await createVoucherMutation.mutateAsync(parsed.data);
    },
  });

  const isPending = createVoucherMutation.isPending;

  return (
    <Dialog
      {...dialog}
      onOpenChange={nextOpenState => {
        if (!nextOpenState) {
          closeCreateVoucherDialog();
        }
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tambah Voucher</DialogTitle>
          <DialogDescription>
            Lengkapi data berikut untuk menambahkan voucher baru.
          </DialogDescription>
        </DialogHeader>

        <form
          className="flex flex-col gap-4"
          onSubmit={e => {
            e.preventDefault();
            e.stopPropagation();
            form.handleSubmit();
          }}
        >
          <form.Field name="voucher_code">
            {field => (
              <div className="grid gap-2">
                <Label htmlFor={field.name}>Kode Voucher</Label>
                <Input
                  id={field.name}
                  placeholder="Masukkan Kode Voucher"
                  maxLength={10}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={e => field.handleChange(e.target.value)}
                />
                <p className="text-xs text-muted-foreground">
                  Maksimal 10 karakter
                </p>
              </div>
            )}
          </form.Field>

          <form.Field name="type">
            {field => (
              <div className="grid gap-2">
                <Label>Tipe Diskon</Label>
                <Select
                  className="w-full"
                  placeholder="Pilih Tipe Diskon"
                  options={TYPE_OPTIONS}
                  value={field.state.value}
                  onValueChange={value =>
                    field.handleChange(value as 'FLAT' | 'PERCENTAGE')
                  }
                />
              </div>
            )}
          </form.Field>

          <form.Subscribe selector={state => [state.values.type]}>
            {([type]) => (
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <form.Field name="discount">
                  {field => (
                    <div className="grid gap-2">
                      <Label htmlFor={field.name}>Diskon</Label>
                      <Input
                        id={field.name}
                        type="number"
                        min={0}
                        max={type === 'PERCENTAGE' ? 100 : undefined}
                        placeholder="0"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={e => {
                          let val = e.target.valueAsNumber || 0;
                          if (type === 'PERCENTAGE' && val > 100) val = 100;
                          field.handleChange(val);
                        }}
                      />
                    </div>
                  )}
                </form.Field>

                <form.Field name="stock">
                  {field => (
                    <div className="grid gap-2">
                      <Label htmlFor={field.name}>Stok</Label>
                      <Input
                        id={field.name}
                        type="number"
                        min={0}
                        placeholder="0"
                        value={field.state.value}
                        onBlur={field.handleBlur}
                        onChange={e =>
                          field.handleChange(e.target.valueAsNumber || 0)
                        }
                      />
                    </div>
                  )}
                </form.Field>
              </div>
            )}
          </form.Subscribe>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <form.Field name="expired_at">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Tanggal Kedaluwarsa</Label>
                  <Input
                    id={field.name}
                    type="date"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>

            <form.Field name="expired_at_time">
              {field => (
                <div className="grid gap-2">
                  <Label htmlFor={field.name}>Jam Kedaluwarsa</Label>
                  <Input
                    id={field.name}
                    type="time"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={e => field.handleChange(e.target.value)}
                  />
                </div>
              )}
            </form.Field>
          </div>

          <form.Field name="target_type">
            {field => (
              <div className="grid gap-2">
                <Label>Berlaku Untuk</Label>
                <Select
                  className="w-full"
                  options={TARGET_TYPE_OPTIONS}
                  value={field.state.value}
                  onValueChange={value =>
                    field.handleChange(value as 'PRODUCT' | 'MENTORING')
                  }
                />
              </div>
            )}
          </form.Field>

          <form.Subscribe selector={state => [state.values.target_type]}>
            {([targetType]) => (
              <form.Field name="scope_mode">
                {field => (
                  <div className="grid gap-2">
                    <Label>Cakupan</Label>
                    <Select
                      className="w-full"
                      options={scopeModeOptions(targetType)}
                      value={field.state.value}
                      onValueChange={value =>
                        field.handleChange(value as 'ALL' | 'WHITELIST')
                      }
                    />
                  </div>
                )}
              </form.Field>
            )}
          </form.Subscribe>

          <form.Subscribe
            selector={state => [
              state.values.target_type,
              state.values.scope_mode,
            ]}
          >
            {([targetType, scopeMode]) =>
              scopeMode === 'WHITELIST' &&
              (targetType === 'PRODUCT' ? (
                <form.Field name="product_ids">
                  {field => (
                    <div className="grid gap-2">
                      <Label>Produk</Label>
                      <VoucherProductSelector
                        value={field.state.value}
                        onChange={field.handleChange}
                      />
                    </div>
                  )}
                </form.Field>
              ) : (
                <form.Field name="mentor_ids">
                  {field => (
                    <div className="grid gap-2">
                      <Label>Mentor</Label>
                      <VoucherMentorSelector
                        value={field.state.value}
                        onChange={field.handleChange}
                      />
                    </div>
                  )}
                </form.Field>
              ))
            }
          </form.Subscribe>

          <DialogFooter className="pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={closeCreateVoucherDialog}
              disabled={isPending}
            >
              Batal
            </Button>
            <form.Subscribe selector={state => [state.canSubmit]}>
              {([canSubmit]) => (
                <Button type="submit" disabled={!canSubmit || isPending}>
                  {isPending ? 'Menyimpan...' : 'Tambah'}
                </Button>
              )}
            </form.Subscribe>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
